import { Phone, MapPin, Clock, Storefront } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatE164 } from "./utils";
import type { BranchStepDraft, OnboardingLabels, OperatingHoursEntry, StepError } from "./types";

export interface BranchStepProps {
  value: BranchStepDraft;
  onChange: (patch: Partial<BranchStepDraft>) => void;
  errors?: StepError[];
  labels: OnboardingLabels;
  defaultOperatingHours?: OperatingHoursEntry[];
  className?: string;
}

const TIMEZONES = [
  "Europe/Istanbul",
  "Europe/Berlin",
  "Europe/London",
  "Asia/Dubai",
];

function findError(errors: StepError[] | undefined, path: string) {
  return errors?.find((e) => e.path === path)?.message;
}

export function BranchStep({ value, onChange, errors, labels, defaultOperatingHours, className }: BranchStepProps) {
  const branchNameError = findError(errors, "branch.branchName");
  const streetError = findError(errors, "branch.address.street");
  const districtError = findError(errors, "branch.address.district");
  const cityError = findError(errors, "branch.address.city");
  const postalError = findError(errors, "branch.address.postalCode");
  const phoneError = findError(errors, "branch.phone");

  return (
    <div className={cn("space-y-6", className)}>
      <FieldShell
        id="onboarding-branch-name"
        label={labels.branch.name}
        required
        error={branchNameError}
        leadingIcon={<Storefront size={16} />}
      >
        <Input
          id="onboarding-branch-name"
          placeholder={labels.branch.namePlaceholder}
          value={value.branchName}
          onChange={(e) => onChange({ branchName: e.target.value })}
          aria-invalid={!!branchNameError}
          className="h-14 pl-10 text-base"
        />
      </FieldShell>

      <fieldset className="space-y-4">
        <legend className="mb-1 inline-flex items-center gap-2 text-sm font-medium text-biz-foreground">
          <MapPin size={16} className="text-biz-foreground-muted" aria-hidden />
          {labels.branch.address.street}
        </legend>

        <FieldShell id="onboarding-street" label={labels.branch.address.street} required error={streetError} hideLabel>
          <Input
            id="onboarding-street"
            placeholder={labels.branch.address.streetPlaceholder}
            value={value.address.street}
            onChange={(e) => onChange({ address: { ...value.address, street: e.target.value } })}
            autoComplete="street-address"
            aria-invalid={!!streetError}
            className="h-14 text-base"
          />
        </FieldShell>

        <div className="grid gap-4 sm:grid-cols-3">
          <FieldShell id="onboarding-district" label={labels.branch.address.district} required error={districtError}>
            <Input
              id="onboarding-district"
              value={value.address.district}
              onChange={(e) => onChange({ address: { ...value.address, district: e.target.value } })}
              aria-invalid={!!districtError}
              className="h-14 text-base"
            />
          </FieldShell>
          <FieldShell id="onboarding-city" label={labels.branch.address.city} required error={cityError}>
            <Input
              id="onboarding-city"
              value={value.address.city}
              onChange={(e) => onChange({ address: { ...value.address, city: e.target.value } })}
              autoComplete="address-level2"
              aria-invalid={!!cityError}
              className="h-14 text-base"
            />
          </FieldShell>
          <FieldShell id="onboarding-postal" label={labels.branch.address.postalCode} required error={postalError}>
            <Input
              id="onboarding-postal"
              inputMode="numeric"
              placeholder={labels.branch.address.postalCodePlaceholder}
              value={value.address.postalCode}
              onChange={(e) => onChange({ address: { ...value.address, postalCode: e.target.value.replace(/\D/g, "").slice(0, 5) } })}
              autoComplete="postal-code"
              aria-invalid={!!postalError}
              className="h-14 font-mono text-base tracking-widest"
            />
          </FieldShell>
        </div>
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-2">
        <FieldShell
          id="onboarding-phone"
          label={labels.branch.phone}
          required
          error={phoneError}
          leadingIcon={<Phone size={16} />}
        >
          <Input
            id="onboarding-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder={labels.branch.phonePlaceholder}
            value={value.phone}
            onChange={(e) => onChange({ phone: formatE164(e.target.value) })}
            aria-invalid={!!phoneError}
            className="h-14 pl-10 font-mono text-base"
          />
        </FieldShell>

        <FieldShell id="onboarding-timezone" label={labels.branch.timezone}>
          <select
            id="onboarding-timezone"
            value={value.timezone}
            onChange={(e) => onChange({ timezone: e.target.value })}
            className={cn(
              "flex h-14 w-full rounded-md border border-biz-input bg-biz-background px-3 text-base text-biz-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2",
            )}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </FieldShell>
      </div>

      {defaultOperatingHours && defaultOperatingHours.length > 0 && (
        <div className="rounded-biz-card border border-biz-border bg-biz-surface p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="inline-flex items-center gap-2 text-sm font-semibold text-biz-foreground">
              <Clock size={16} className="text-biz-primary" aria-hidden />
              {labels.branch.operatingHours}
            </p>
            <span className="rounded-full bg-biz-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-biz-foreground-muted">
              preview
            </span>
          </div>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4 lg:grid-cols-7">
            {defaultOperatingHours.map((entry) => (
              <li key={entry.weekday} className="flex flex-col text-xs">
                <span className="font-semibold uppercase tracking-wide text-biz-foreground-muted">
                  {labels.branch.weekdays[entry.weekday]}
                </span>
                <span className="font-mono text-sm text-biz-foreground">
                  {entry.closed ? labels.branch.closed : `${entry.open}–${entry.close}`}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-biz-foreground-muted">{labels.branch.operatingHoursHint}</p>
        </div>
      )}
    </div>
  );
}

interface FieldShellProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  leadingIcon?: React.ReactNode;
  hideLabel?: boolean;
  children: React.ReactNode;
}

function FieldShell({ id, label, required, error, leadingIcon, hideLabel, children }: FieldShellProps) {
  return (
    <div>
      <Label
        htmlFor={id}
        className={cn(
          "mb-1.5 inline-flex items-center gap-1 text-sm font-medium text-biz-foreground",
          hideLabel && "sr-only",
        )}
      >
        {label}
        {required && <span className="text-biz-danger">*</span>}
      </Label>
      <div className="relative">
        {leadingIcon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-biz-foreground-muted">
            {leadingIcon}
          </span>
        )}
        {children}
      </div>
      {error && (
        <p role="alert" className="mt-1.5 text-xs font-medium text-biz-danger">
          {error}
        </p>
      )}
    </div>
  );
}
